const PORTAL_RESERVED_SEGMENTS = new Set(['404', '500', 'article', 'customer-portal']);

export function normalizePortalPath(urlOrPath: string): string {
    return urlOrPath.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
}

export function isPortalRootPath(normalizedPath: string): boolean {
    return normalizedPath === '/' || normalizedPath === '/customer-portal';
}

export function getPortalPathSegments(urlOrPath: string): string[] {
    let rest = normalizePortalPath(urlOrPath);
    if (rest.startsWith('/customer-portal/')) {
        rest = rest.slice('/customer-portal'.length) || '/';
    } else if (rest === '/customer-portal') {
        return [];
    }

    return rest.split('/').filter(Boolean);
}

export function parseKbSlugOnlyPath(normalizedPath: string): string | null {
    if (isPortalRootPath(normalizedPath)) {
        return null;
    }

    const segments = getPortalPathSegments(normalizedPath);
    if (segments.length !== 1) {
        return null;
    }

    const slug = segments[0];
    return PORTAL_RESERVED_SEGMENTS.has(slug) ? null : slug;
}

export function shouldApplyPortalDefaultLangRedirect(normalizedPath: string): boolean {
    return isPortalRootPath(normalizedPath) || parseKbSlugOnlyPath(normalizedPath) !== null;
}

export function buildPortalDefaultLangRedirectPath(
    normalizedPath: string,
    defaultKbSlug: string | undefined,
    defaultLanguage: string | undefined,
    search = '',
): string | null {
    if (!defaultLanguage) {
        return null;
    }

    if (isPortalRootPath(normalizedPath)) {
        if (!defaultKbSlug) {
            return null;
        }
        return `/${defaultKbSlug}/${defaultLanguage}${search}`;
    }

    const slugOnly = parseKbSlugOnlyPath(normalizedPath);
    if (!slugOnly) {
        return null;
    }

    return `/${slugOnly}/${defaultLanguage}${search}`;
}

/**
 * Legacy paths omit kb_slug: /en/article/foo → /{defaultKbSlug}/en/article/foo
 */
export function buildLegacyMissingKbSlugRedirectPath(
    urlOrPath: string,
    defaultKbSlug: string | undefined,
): string | null {
    if (!defaultKbSlug) {
        return null;
    }

    const segments = getPortalPathSegments(urlOrPath);
    if (!segments.length || segments[0] === defaultKbSlug) {
        return null;
    }

    if (PORTAL_RESERVED_SEGMENTS.has(segments[0])) {
        return null;
    }

    const second = segments[1];
    // /{lang}/article/... or /{lang}/category/...
    if (second === 'article' || second === 'category') {
        const searchIndex = urlOrPath.indexOf('?');
        const search = searchIndex >= 0 ? urlOrPath.slice(searchIndex) : '';
        return `/${defaultKbSlug}/${segments.join('/')}${search}`;
    }

    return null;
}
